import api from '../api/axios';
import axios from 'axios';

export class FileDataService {
  /**
   * Resizes and compresses an image file.
   * @param file The original file.
   * @param maxDimension The maximum width or height.
   * @param quality JPEG quality (0 to 1).
   * @returns A Promise resolving to a Blob.
   */
  static resizeImage(file: File, maxDimension: number, quality: number, outputMimeType?: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;

        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const mimeType = outputMimeType ?? 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas toBlob failed'));
            }
            URL.revokeObjectURL(img.src);
          },
          mimeType,
          quality
        );
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(img.src);
        reject(err);
      };
    });
  }

  /**
   * Gets a presigned URL for uploading a file to S3.
   * @param fileName The name of the file.
   * @param fileType The MIME type of the file.
   * @param childUniqueId The identifier for the child (UUID).
   * @param uploadCategory Optional category: 'profile' uses Profiles/ prefix, 'post' (default) uses Uploads/ prefix.
   */
  static async getPresignedUrl(
    fileName: string,
    fileType: string,
    childUniqueId: string,
    uploadCategory?: 'profile' | 'post' | 'org-logo' | 'document' | 'org-document'
  ): Promise<{ signedUrl: string; fileKey: string }> {
    const response = await api.post('/upload/get-presigned-url', {
      fileName,
      fileType,
      childUniqueId,
      ...(uploadCategory ? { uploadCategory } : {}),
    });
    return response.data;
  }

  /**
   * Uploads a file (Blob) to S3 using a presigned URL.
   * @param signedUrl The presigned URL.
   * @param file The file content as a Blob.
   */
  static async uploadToS3(signedUrl: string, file: Blob): Promise<void> {
    // Create a clean axios instance to avoid global defaults/interceptors
    const instance = axios.create();
    
    // Explicitly nullify Authorization header to prevent CORS issues if it somehow leaks
    delete instance.defaults.headers.common['Authorization'];

    await instance.put(signedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      // Prevent axios from adding XSRF tokens etc
      xsrfCookieName: undefined,
      xsrfHeaderName: undefined,
    });
  }
}
